import { useId, useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import type { Translate } from "../composeMessage";
import type { StampView } from "../state";

gsap.registerPlugin(useGSAP);

/**
 * La cartolina: il riepilogo che si compone mentre si sceglie.
 *
 * È solo OUTPUT, mai un insieme di campi: a sinistra il testo — carattere per
 * carattere quello che parte nel campo `message` —, a destra i francobolli con
 * le foto delle confezioni scelte e le righe dell'indirizzo con i recapiti.
 * All'invio riuscito viene timbrata (l'annullo GB). Francobolli, righe e timbro
 * sono decorativi (`aria-hidden`): l'informazione è tutta nel testo.
 */

interface PostcardProps {
  t: Translate;
  /** «Preferisco scrivere»: la cartolina vuota invita a scrivere, non a scegliere un argomento. */
  free?: boolean;
  message: string;
  headline: string | null;
  name: string;
  company: string;
  email: string;
  phone: string;
  stamps: StampView[];
  stamped?: boolean;
}

const MAX_STAMPS = 3;
const motionOK = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
  } catch {
    return false;
  }
};

export function Postcard({ t, free = false, message, headline, name, company, email, phone, stamps, stamped = false }: PostcardProps) {
  const uid = useId();
  const capId = `${uid}-cap`;
  const ref = useRef<HTMLElement>(null);
  const prevSlugs = useRef<string[] | null>(null);
  const stampKey = stamps.map((s) => s.slug).join("|");

  // Un francobollo nuovo si «posa» sulla cartolina (0,32 s). Niente al primo disegno.
  useGSAP(() => {
    const now = stampKey ? stampKey.split("|") : [];
    const before = prevSlugs.current;
    prevSlugs.current = now;
    if (!before || !motionOK()) return;
    for (const slug of now.filter((s) => !before.includes(s))) {
      gsap.from(`[data-stamp="${slug}"]`, { opacity: 0, scale: 0.6, rotate: -14, y: -10, duration: 0.32, ease: "expo.out", clearProps: "transform,opacity" });
    }
  }, { scope: ref, dependencies: [stampKey], revertOnUpdate: true });

  // Il timbro: anelli, onde e spunta che si disegnano entro 0,4 s.
  useGSAP(() => {
    if (!stamped || !motionOK()) return;
    const tl = gsap.timeline();
    tl.fromTo(".rq-pm-ring", { opacity: 0, scale: 1.12, transformOrigin: "50% 50%" }, { opacity: 1, scale: 1, duration: 0.18, ease: "power2.out" }, 0)
      .fromTo(".rq-pm-wave", { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 0.22, ease: "power2.out", stagger: 0.04 }, 0.06)
      .fromTo(".rq-pm-check", { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 0.22, ease: "power2.out" }, 0.16);
  }, { scope: ref, dependencies: [stamped] });

  const head = headline && message.startsWith(headline) ? headline : null;
  const rest = head ? message.slice(head.length) : message;
  const firstLine = [name.trim(), company.trim()].filter(Boolean).join(" · ");
  const shown = stamps.slice(0, MAX_STAMPS);
  const more = stamps.length - shown.length;

  return (
    <figure ref={ref} className="rq-card" aria-labelledby={capId} data-gb="cartolina">
      <figcaption id={capId} className="rq-card__cap">
        <span className="rq-overline">{t("contact.guide.card.overline")}</span>
        <span className="rq-card__capline">{t("contact.guide.card.caption")}</span>
      </figcaption>
      <div className="rq-card__paper">
        <p className="rq-card__printed" aria-hidden="true">{t("contact.guide.card.printed")}</p>
        <div className="rq-card__stamps" aria-hidden="true">
          <div className="rq-stamps">
            {shown.length ? (
              shown.map((s) => <Stamp key={s.slug} stamp={s} />)
            ) : (
              <span className="rq-stamp rq-stamp--gb">
                <span className="rq-stamp__inner">
                  <span className="rq-stamp__gb">GB</span>
                  <span className="rq-stamp__brand">GRANBORGO</span>
                </span>
              </span>
            )}
          </div>
          {more > 0 && <span className="rq-stamp__more">+{more}</span>}
        </div>
        <div className="rq-card__text" role="region" aria-label={t("contact.guide.a11y.messageRegion")} tabIndex={0} data-gb="testo-cartolina">
          {message ? (
            <>
              {head && <span className="rq-card__head">{head}</span>}
              {rest}
            </>
          ) : (
            <span className="rq-card__empty">{t(free ? "contact.guide.card.emptyFree" : "contact.guide.card.empty")}</span>
          )}
        </div>
        <span className="rq-card__divider" aria-hidden="true" />
        <div className="rq-card__addr">
          <p className="rq-card__line">
            <span className="rq-card__lbl">{t("contact.guide.card.to")}</span>
            {t("contact.guide.card.toName")}
          </p>
          <p className="rq-card__line">
            <span className="rq-card__lbl">{t("contact.guide.card.from")}</span>
            {firstLine || (!email.trim() ? <span className="rq-card__ph">{t("contact.guide.card.fromEmpty")}</span> : null)}
          </p>
          <p className="rq-card__line">{email.trim()}</p>
          <p className="rq-card__line">{phone.trim()}</p>
        </div>
        {stamped && <Postmark />}
      </div>
    </figure>
  );
}

function Stamp({ stamp }: { stamp: StampView }) {
  return (
    <span className="rq-stamp" data-stamp={stamp.slug}>
      <span className="rq-stamp__inner">
        {stamp.imageUrl ? (
          <img className="rq-stamp__img" src={stamp.imageUrl} alt="" width={140} height={120} loading="lazy" decoding="async" />
        ) : (
          <span className="rq-stamp__gb rq-stamp__gb--fill">GB</span>
        )}
      </span>
    </span>
  );
}

/** Annullo circolare con onde e la spunta del successo del checkout (aria-hidden). */
function Postmark() {
  const waves = [30, 40, 50];
  return (
    <svg className="rq-postmark" viewBox="0 0 132 80" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {waves.map((y) => (
        <path key={y} className="rq-pm-wave" d={`M2 ${y}c6-4 12-4 18 0s12 4 18 0 12-4 18 0 12 4 18 0`} strokeWidth="1.4" pathLength={1} strokeDasharray="1" />
      ))}
      <circle className="rq-pm-ring" cx="96" cy="40" r="34" strokeWidth="1.6" />
      <circle className="rq-pm-ring" cx="96" cy="40" r="26" strokeWidth="1" />
      <path className="rq-pm-check" d="M84.5 40.5l7.5 7.5 15-16" strokeWidth="3.4" pathLength={1} strokeDasharray="1" />
    </svg>
  );
}

/** Versione compatta, in cima ai passi su schermi stretti e nel pannello: solo testo, niente foto illeggibili. */
export function PostcardMini({ t, headline, note, stampsCount, top }: { t: Translate; headline: string | null; note: string; stampsCount: number; top: number }) {
  const uid = useId();
  const firstNoteLine = note.trim().split("\n")[0] ?? "";
  const text = headline ?? firstNoteLine;
  return (
    <div className="rq-mini" role="group" aria-labelledby={`${uid}-l`} style={{ ["--rq-mini-top" as string]: `${top}px` }} data-gb="mini-cartolina">
      <div className="rq-mini__body">
        <span id={`${uid}-l`} className="rq-mini__label">{t("contact.guide.card.overline")}</span>
        <span className="rq-mini__text" data-empty={text ? undefined : ""}>{text || t("contact.guide.card.miniEmpty")}</span>
      </div>
      <span className="rq-mini__stamp" aria-hidden="true">{stampsCount > 0 ? `×${stampsCount}` : "GB"}</span>
    </div>
  );
}
